/**
 * Signed-in chrome: a sidebar on desktop, a bottom tab bar on phones.
 *
 * Dashbase is used heavily on iPhone, so navigation is thumb-reachable at the
 * bottom below the `md` breakpoint and the content area owns its own scroll.
 */
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  CalendarClock,
  CheckSquare,
  Bookmark,
  Timer,
  Target,
  StickyNote,
  ListChecks,
  Settings,
  LayoutDashboard,
  Terminal,
  Moon,
  Sun,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/components/theme-provider";
import { useIsOffline } from "@/hooks/use-is-offline";
import { WorkModeSwitcher } from "@/components/work-mode-switcher";
import { GlobalSearch } from "@/components/global-search";
import { UserMenu } from "@/components/user-menu";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /** Shown in the phone tab bar. Keeps the bar to six items. */
  primary?: boolean;
}

const NAV: NavItem[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, primary: true },
  { to: "/dashplan", label: "Dashplan", icon: CalendarClock, primary: true },
  { to: "/dailies", label: "Dailies", icon: CheckSquare, primary: true },
  { to: "/bookmarks", label: "Bookmarks", icon: Bookmark, primary: true },
  { to: "/focus", label: "Focus", icon: Timer, primary: true },
  { to: "/quests", label: "Quests", icon: Target },
  { to: "/notes", label: "Notes", icon: StickyNote },
  { to: "/lists", label: "Lists", icon: ListChecks },
  { to: "/settings", label: "Settings", icon: Settings, primary: true },
];

export function AppShell() {
  const { resolvedTheme, setTheme } = useTheme();
  const location = useLocation();
  const isOffline = useIsOffline();

  return (
    <div
      className={cn(
        "min-h-screen flex flex-col md:flex-row",
        isOffline && "is-offline",
      )}
    >
      {/* Desktop sidebar.
          `sticky` + `h-dvh` pins it to the viewport. Without them it is just a
          flex child of a container as tall as the page, so on a long route —
          Dashplan's timeline is roughly 1900px — it stretched to the full
          document height and its footer, with the account menu and the theme
          toggle, sat at the bottom of the *document*. You had to scroll to the
          end of the day to reach your own avatar, and only on the long routes,
          which is what made it look like a per-page bug.
          `dvh` rather than `vh` so iPad Safari's collapsing toolbars do not
          push the footer under the chrome. */}
      <aside
        className="hidden md:flex md:sticky md:top-0 md:h-dvh md:w-60 lg:w-64 shrink-0 flex-col border-r border-border bg-card/40 backdrop-blur-sm"
        style={{
          paddingTop: "env(safe-area-inset-top, 0px)",
          paddingLeft: "env(safe-area-inset-left, 0px)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        <div className="h-16 flex items-center gap-3 px-5 border-b border-border">
          <div className="w-9 h-9 brand-tile rounded-lg flex items-center justify-center shrink-0">
            <Terminal className="w-5 h-5" />
          </div>
          <span className="font-bold code-font tracking-tight">DASHBASE</span>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                  isActive
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )
              }
            >
              <Icon className="w-4 h-4 shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-border flex items-center justify-between gap-2">
          <UserMenu />
          <Button
            variant="ghost"
            size="icon"
            aria-label={
              resolvedTheme === "dark"
                ? "Switch to light theme"
                : "Switch to dark theme"
            }
            onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
          >
            {resolvedTheme === "dark" ? (
              <Sun className="w-4 h-4" />
            ) : (
              <Moon className="w-4 h-4" />
            )}
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header
          className="shrink-0 flex items-center gap-3 px-4 md:px-6 border-b border-border bg-background/80 backdrop-blur-sm sticky top-0 z-30"
          style={{
            /* The bar's own background has to fill the notch zone, so the
               inset goes into its padding rather than above it. On desktop the
               sidebar already absorbed the top inset, but this header is the
               first element on phones. */
            height: "calc(4rem + env(safe-area-inset-top, 0px))",
            paddingTop: "env(safe-area-inset-top, 0px)",
            paddingLeft: "calc(1rem + env(safe-area-inset-left, 0px))",
            paddingRight: "calc(1rem + env(safe-area-inset-right, 0px))",
          }}
        >
          <div className="md:hidden w-8 h-8 brand-tile rounded-lg flex items-center justify-center shrink-0">
            <Terminal className="w-4 h-4" />
          </div>

          <GlobalSearch />

          <div className="ml-auto flex items-center gap-2">
            <WorkModeSwitcher />
            <div className="md:hidden">
              <UserMenu />
            </div>
          </div>
        </header>

        {/* `key` remounts the page on navigation so per-page state (a half-typed
            form, a scrolled timeline) never leaks between routes. */}
        <main
          key={location.pathname}
          className="flex-1 min-w-0 px-4 md:px-6 py-5 pb-24 md:pb-8"
          style={{
            paddingLeft: "calc(1rem + env(safe-area-inset-left, 0px))",
            paddingRight: "calc(1rem + env(safe-area-inset-right, 0px))",
          }}
        >
          <Outlet />
        </main>
      </div>

      {/* Phone tab bar */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/95 backdrop-blur-md"
        style={{
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          paddingLeft: "env(safe-area-inset-left, 0px)",
          paddingRight: "env(safe-area-inset-right, 0px)",
        }}
      >
        <div className="grid grid-cols-6">
          {NAV.filter((item) => item.primary).map(
            ({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                className={({ isActive }) =>
                  cn(
                    "flex flex-col items-center gap-1 py-2.5 text-[10px] transition-colors",
                    isActive
                      ? "text-primary"
                      : "text-muted-foreground active:text-foreground",
                  )
                }
              >
                <Icon className="w-5 h-5" />
                <span className="leading-none">{label}</span>
              </NavLink>
            ),
          )}
        </div>
      </nav>
    </div>
  );
}
